import * as React from 'react';
import { useEffect } from 'react';
import { QueryClientProvider } from 'react-query';
import { Provider, useDispatch, useSelector } from 'react-redux';

import { config } from '@fortawesome/fontawesome-svg-core';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { Security } from '@okta/okta-react';
import { ConfigProvider } from 'antd';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import type { AppContext, AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import Error from 'next/error';
import Head from 'next/head';
import Router from 'next/router';
import numeral from 'numeral';
import { Reshaped } from 'reshaped';

import { client } from '@portal/api-client/src/client';

import { AccessGuard } from '@/guards/access.guard';

import GlobalLoader from '../components/global-loader';
import { antdTheme } from '../config/antd';
import { queryClient } from '../config/query-client';
import { getEnv } from '../envWrapper';
import { AuthenticatedRouteGuard, AuthenticationGuard } from '../guards';
import { selectConfig } from '../resources/config/selects';
import { setIsServer } from '../store/config';
import { initializeStore } from '../store/index';
import type { ApplicationState } from '../store/index';
import {
  useDataDog,
  useGoogleAnalytics,
  useHotjar,
  useSentry,
} from '../utils/hooks';
import { LoggerFactory } from '../utils/logger';
import { createErrorInterceptor } from '../utils/rest-client-factory';

// CSS Layers - define layer order so Reshaped's rs.reset has lowest priority
import '@/styles/layers.css';

// Reshaped theme CSS (uses its own internal layers: rs.reset, rs.theme, rs.utilities)
import 'reshaped/themes/reshaped/theme.css';

// Third-party CSS imports (unlayered, so they win over Reshaped's layered resets)
import 'normalize.css';
import '@blueprintjs/core/lib/css/blueprint.css';
import '@blueprintjs/select/lib/css/blueprint-select.css';
import '@blueprintjs/datetime/lib/css/blueprint-datetime.css';
import '@fortawesome/fontawesome-svg-core/styles.css';

import '@/styles/globalStyles.scss';

const ContentWrapper = dynamic(() => import('./contentWrapper'), {
  ssr: false,
});
const Feedback = dynamic(() => import('./feedback/feedback'), { ssr: false });
const LegalAgreementModal = dynamic(
  () =>
    import('@/components/legal-agreement-modal').then((mod) => ({
      default: mod.LegalAgreementModal,
    })),
  { ssr: false }
);

const ReactQueryDevtools = dynamic(
  () => import('react-query/devtools').then((mod) => mod.ReactQueryDevtools),
  { ssr: false }
);

dayjs.extend(isoWeek);

// Numeral.js defaults
numeral.nullFormat('--');

config.autoAddCss = false;

const env = getEnv();
client.defaults.baseURL = env.PORTAL_API_URL;
client.interceptors.response.use(
  undefined,
  createErrorInterceptor(LoggerFactory('CCApiClient', env))
);

interface AppStateProps {
  statusCode?: number;
}

const isClient = typeof window !== 'undefined';
const isE2ETest = env.NEXT_PUBLIC_E2E_TEST;

const oktaAuth = new OktaAuth({
  clientId: env.NEXT_PUBLIC_OKTA_CLIENT_ID,
  issuer: `${env.NEXT_PUBLIC_OKTA_ORG_URL}/oauth2/default`,
  postLogoutRedirectUri: isClient ? `${window.location.origin}/sign-out` : '',
  redirectUri: isClient ? `${window.location.origin}/sign-in-callback` : '',
  ...(isE2ETest && {
    tokenManager: {
      autoRemove: false,
      autoRenew: false,
    },
  }),
});

const restoreOriginalUri = async (_oktaAuth: OktaAuth, originalUri: string) => {
  if (isClient) {
    const relativeUrl = toRelativeUrl(
      originalUri || '/',
      window.location.origin
    );
    Router.push(relativeUrl);
  }
};

const Dynamic = ({ children }: { children: React.ReactNode }) => {
  const [hasMounted, setHasMounted] = React.useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  if (!hasMounted) {
    return null;
  }

  return <>{children}</>;
};

interface CustomPageProps {
  [key: string]: any;
  initialReduxState?: Partial<ApplicationState>;
  withoutLayout?: boolean;
}

function App({
  Component,
  pageProps,
  statusCode,
}: AppProps & AppStateProps & { pageProps: CustomPageProps }) {
  const dispatch = useDispatch();
  const config = useSelector(selectConfig);
  const agGridLicense = config.AG_GRID_LICENSE;

  useEffect(() => {
    dispatch(setIsServer(false));

    // Lazy load Blueprint FocusStyleManager
    import('@blueprintjs/core').then(({ FocusStyleManager }) => {
      FocusStyleManager.onlyShowFocusOnTabs();
    });
  }, [dispatch]);

  useEffect(() => {
    // Lazy load AG Grid license (only needed when AG Grid is used)
    if (agGridLicense) {
      import('../utils/ag-grid').then(({ setAGGridLicense }) => {
        setAGGridLicense({ AG_GRID_LICENSE: agGridLicense });
      });
    }
  }, [agGridLicense]);

  useDataDog();
  useGoogleAnalytics();
  useHotjar();
  useSentry();

  const { withoutLayout } = pageProps;

  return (
    <Dynamic>
      <Head>
        <title>Engine by MoneyLion | Control Center</title>
        <meta content="#ffffff" name="msapplication-TileColor" />
        <meta
          content="/static/ms-icon-144x144.png"
          name="msapplication-TileImage"
        />
        <meta content="#ffffff" name="theme-color" />
        <meta content="width=1024" name="viewport" />

        <link href="/static/favicon.ico" rel="icon" />
        <link
          href="/static/favicon-96x96.png"
          rel="icon"
          sizes="96x96"
          type="image/png"
        />
        <link
          href="/static/favicon-32x32.png"
          rel="icon"
          sizes="32x32"
          type="image/png"
        />
        <link
          href="/static/favicon-16x16.png"
          rel="icon"
          sizes="16x16"
          type="image/png"
        />
        <link href="/static/manifest.json" rel="manifest" />
      </Head>

      <AuthenticationGuard>
        <AccessGuard>
          <ConfigProvider theme={antdTheme}>
            <AuthenticatedRouteGuard>
              <GlobalLoader />
              <LegalAgreementModal />
              <Feedback />
            </AuthenticatedRouteGuard>

            <Reshaped scoped theme="reshaped">
              <ContentWrapper withoutLayout={!!withoutLayout}>
                {statusCode === 404 ? (
                  <Error statusCode={404} />
                ) : (
                  <Component {...pageProps} />
                )}
              </ContentWrapper>
            </Reshaped>
          </ConfigProvider>
        </AccessGuard>
      </AuthenticationGuard>

      {/* https://stackoverflow.com/questions/14389566/stop-css-transition-from-firing-on-page-load/42969608#42969608 */}
      {/* eslint:disable-next-line */}
      <script> </script>
    </Dynamic>
  );
}

export const AppWithProviders = (
  props: AppProps & AppStateProps & { pageProps: CustomPageProps }
) => {
  // Only use initialReduxState on first render to avoid recreating store on navigation
  const initialStateRef = React.useRef(props.pageProps?.initialReduxState);

  const store = React.useMemo(
    () => initializeStore(initialStateRef.current),
    []
  );
  return (
    <Provider store={store}>
      <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
        <QueryClientProvider client={queryClient}>
          <App {...props} />
          {!env.IS_PRODUCTION_ENV && <ReactQueryDevtools />}
        </QueryClientProvider>
      </Security>
    </Provider>
  );
};

AppWithProviders.getInitialProps = async (context: AppContext) => {
  const {
    Component,
    ctx,
    ctx: { req, res },
  } = context;

  if (res && req?.headers.host?.includes('portal')) {
    res.writeHead(307, { Location: 'https://controls.engine.tech' }).end();
  }

  const pageProps = {
    ...(Component.getInitialProps ? await Component.getInitialProps(ctx) : {}),
    initialReduxState: {
      config: getEnv(),
    },
  };

  return {
    pageProps,
    statusCode: ctx?.res?.statusCode,
  };
};

export default AppWithProviders;
